import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import AskAsperIcon from '../../../atoms/Icon/AskAsperIcon';

export interface AssessmentMessageProps {
    status?: string; // e.g., "Assessment Complete!"
    content?: string; // Supports **bold** markers
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * AssessmentMessage - Displays final assessment message with AskAsper icon
 * 
 * This component displays the final assessment/gist message that appears after
 * all phase cards are complete. It shows:
 * - AskAsper icon and name
 * - Assessment status (e.g., "Assessment Complete!")
 * - Assessment content with bold text support
 */
const AssessmentMessage: React.FC<AssessmentMessageProps> = ({
    status,
    content,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();

    if (!status && !content) {
        return null;
    }

    // Render text content with bold markers and line breaks
    const renderTextContent = (text: string) => {
        return (
            <div className="whitespace-pre-wrap">
                {text.split('\n').map((line, index, array) => {
                    // Handle bold text with ** markers
                    const parts = line.split(/(\*\*.*?\*\*)/g);
                    return (
                        <React.Fragment key={index}>
                            {parts.map((part, partIndex) => {
                                if (part.startsWith('**') && part.endsWith('**')) {
                                    const boldText = part.slice(2, -2);
                                    return (
                                        <strong key={partIndex} className="font-semibold">
                                            {boldText}
                                        </strong>
                                    );
                                }
                                return <span key={partIndex}>{part}</span>;
                            })}
                            {index < array.length - 1 && <br />}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex gap-3"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-assessment-message
            >
                {/* Avatar/Icon */}
                <div className="flex-shrink-0">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                        <div className="scale-[0.4] origin-center">
                            <AskAsperIcon size={48} />
                        </div>
                    </div>
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                    {/* Assessment Status */}
                    {status && (
                        <div className="font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)]">
                            {renderTextContent(status)}
                        </div>
                    )}

                    {/* Assessment Content */}
                    {content && (
                        <div className={`font-['Open_Sans'] text-[16px] leading-[24px] text-[rgba(0,0,0,0.88)] ${status ? 'mt-2' : ''}`}>
                            {renderTextContent(content)}
                        </div>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default AssessmentMessage;
